// packagesStore.ts
import { create } from 'zustand';
import { BillingPackage } from '../types/BillingPackage';
import { getAllPackages, getServicePackages, createPackage as createPackageService, deletePackage as deletePackageService, updatePackage as updatePackageService } from '../services';

type PackagesState = {
  packages: BillingPackage[];
  loading: boolean;
  error: string | null;

  fetchAllPackages: () => Promise<void>;
  fetchServicePackages: (serviceName: string) => Promise<void>;
  createPackage: (packageData: any) => Promise<void>;
  deletePackage: (id: string) => Promise<{ success: boolean; message: string }>;
  updatePackage: (
    id: string,
    updatedData: Partial<BillingPackage>
  ) => Promise<ApiResponse>;
};

type ApiResponse<T = any> = {
  status: "success" | "error";
  message: string;
  data?: T;
};

export const usePackagesStore = create<PackagesState>((set) => ({
  packages: [],
  loading: false,
  error: null,

  fetchAllPackages: async () => {
    set({ loading: true, error: null });
    try {
      const data = await getAllPackages();
      set({ packages: data, loading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch packages', loading: false });
    }
  },

  fetchServicePackages: async (serviceName) => {
    set({ loading: true, error: null });
    try {
      const data = await getServicePackages(serviceName);
      set({ packages: data, loading: false });
    } catch (err: any) {
      set({ error: err.message || 'Failed to fetch service packages', loading: false });
    }
  },

  createPackage: async (packageData) => {
    set({ loading: true, error: null });
    try {
      const response = await createPackageService(packageData);
      const updated = await getAllPackages();
      set({ packages: updated, loading: false });
      return response;
    } catch (err: any) {
      set({ error: err.message || 'Failed to create package', loading: false });
      throw err;
    }
  },

  deletePackage: async (id) => {
    try {
      await deletePackageService(id);
      set((state) => ({
        packages: state.packages.filter((pkg) => pkg._id !== id),
      }));
      return { success: true, message: "Package deleted successfully" };
    } catch (error: any) {
      return { success: false, message: error?.message || "Failed to delete package" };
    }
  },

  updatePackage: async (id: string, updatedData: Partial<BillingPackage>): Promise<ApiResponse> => {
    try {
      const dataWithId = { ...updatedData, _id: id };
      const res: ApiResponse = await updatePackageService(dataWithId);
  
      if (res.status === "success") {
        await getAllPackages();
      }
  
      return res;
    } catch (err) {
      return { status: "error", message: "Error updating package" };
    }
  }

}));
